import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { Box, CardContent, CardHeader, styled, Typography } from '@mui/material';

import { coursesApi } from 'Api';
import { LoginState, useSelectLoginState } from 'Auth/data';
import { DateInfo } from 'Common/DateInfo';
import { Link, Logo } from 'Components';
import { StyledButton, StyledLogo } from 'Components/Styled';
import { urls } from 'Router';
import { Spacing } from 'Themes/spacing';
import { Typo } from 'Themes/typo';

import { TimeInfo } from '@common/TimeInfo';
import { DEFAULT_TIMEZONE } from '@consts/timezone';
import type { IJoinToCourseRequest } from '@libs/models';
import { useSelectUserTimezone } from '@user/data';

import { ButtonLink, StyledJoinCourseSkeleton } from './JoinToCoursePage.styles';

const Content = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.medium};
`;

const ActionButtonsContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacing.medium};
`;

const LinksContainer = styled(Box)`
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  margin-top: ${Spacing.xlarge};
  gap: ${Spacing.small};
`;

type Props = {
  shouldSkipConfirmation: boolean;
};

export const JoinToCoursePage = ({ shouldSkipConfirmation }: Readonly<Props>) => {
  const { joinCode } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userTimezone = useSelectUserTimezone();
  const displayTimezone = userTimezone ?? DEFAULT_TIMEZONE;

  const loginState = useSelectLoginState();

  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [courseTitle, setCourseTitle] = useState<string | null | undefined>(null);
  const [courseStartDate, setCourseStartDate] = useState<string | null | undefined>(null);
  const [courseErrorTranslationCode, setCourseErrorTranslationCode] = useState<
    string | null | undefined
  >(null);

  const handleYesClick = async () => {
    try {
      const result = await coursesApi.joinCourse({ joinCode } as IJoinToCourseRequest);
      if (result.isSuccess) {
        const data = result.data;
        setCourseErrorTranslationCode(data?.errorTranslationCode);

        if (!data?.errorTranslationCode) {
          navigate(urls.homePage);
        }
      } else {
        setCourseErrorTranslationCode(result.error);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setCourseErrorTranslationCode(errorMessage);
    }
  };

  const handleLoginClick = () => navigate(urls.loginPage, { state: { joinCode, courseTitle } });

  const handleRegisterClick = () =>
    navigate(urls.registrationPage, { state: { joinCode, courseTitle } });

  const checkStatusAndJoinToCourse = async () => {
    if (!joinCode) {
      setIsDataLoaded(true);
      return;
    }

    try {
      const result = await coursesApi.getCourseStatus({ joinCode });
      if (result.isSuccess && result.data) {
        const data = result.data;
        setCourseTitle(data.title);
        setCourseStartDate(data.startDate);
        setCourseErrorTranslationCode(data.errorTranslationCode);

        if (!data.errorTranslationCode && shouldSkipConfirmation) {
          handleYesClick();
        }

        setIsDataLoaded(true);
      } else {
        if (result.error !== 'REQUEST_CANCELED') {
          setCourseErrorTranslationCode(result.error);
        }

        setIsDataLoaded(true);
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError' && error.message !== 'canceled') {
        setCourseErrorTranslationCode(error.message);
      }

      setIsDataLoaded(true);
    }
  };

  useEffect(() => {
    if (courseErrorTranslationCode === 'joined_already') {
      navigate(urls.homePage);
    }
  }, [courseErrorTranslationCode, navigate]);

  useEffect(() => {
    checkStatusAndJoinToCourse();
  }, [joinCode]);

  const renderContent = () => {
    if (courseErrorTranslationCode) {
      return (
        <>
          {courseTitle && <CardHeader title={courseTitle}></CardHeader>}
          <CardHeader
            title={courseErrorTranslationCode ? t(courseErrorTranslationCode) : ''}
          ></CardHeader>

          <CardContent>
            <ButtonLink onClick={checkStatusAndJoinToCourse}>{t('auth.tryAgain')}</ButtonLink>
          </CardContent>
        </>
      );
    }

    if (loginState !== LoginState.LOGGED) {
      return (
        <Content>
          <div>{t('auth.toJoinTheTraining')}</div>
          <Typography variant={Typo.bigHeader}>{courseTitle}</Typography>
          <div>
            {t('auth.starting')}{' '}
            {courseStartDate && (
              <span>
                <DateInfo date={courseStartDate} timezone={displayTimezone} />{' '}
                <TimeInfo date={courseStartDate} timezone={displayTimezone} />
              </span>
            )}
          </div>

          <ActionButtonsContainer>
            <StyledButton onClick={handleLoginClick}>{t('auth.login')}</StyledButton>

            <Typography noWrap>{t('auth.or')}</Typography>

            <StyledButton variant="outlined" onClick={handleRegisterClick}>
              {t('auth.signUp')}
            </StyledButton>
          </ActionButtonsContainer>

          <LinksContainer>
            <Link
              to={urls.forgotPasswordPage}
              label={t('auth.remindPassword')}
              shouldOpenInNewTab={false}
            />
            <Link to={urls.treneoSite} label={t('auth.whatIsTreneo')} />
          </LinksContainer>
        </Content>
      );
    }

    return (
      <Content>
        {!shouldSkipConfirmation && (
          <>
            <div>{t('auth.course')}</div>
            <Typography variant={Typo.bigHeader}>{courseTitle}</Typography>
            <div>
              {t('auth.starts')}{' '}
              {courseStartDate && (
                <span>
                  <DateInfo date={courseStartDate} timezone={displayTimezone} />{' '}
                  <TimeInfo date={courseStartDate} timezone={displayTimezone} />
                </span>
              )}
            </div>

            <StyledButton onClick={handleYesClick}>{t('auth.join')}</StyledButton>
          </>
        )}
      </Content>
    );
  };

  return isDataLoaded ? (
    <>
      <StyledLogo>
        <Typography noWrap>{t('auth.welcomeIn')}</Typography>
        <Logo variant="login" />
      </StyledLogo>
      {renderContent()}
    </>
  ) : (
    <>
      <StyledJoinCourseSkeleton />
    </>
  );
};
